import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { OrderManagementPage } from '@/pages/OrderManagementPage';
import { TenantManagementPage } from '@/pages/TenantManagementPage';
import { WorkspacePage } from '@/pages/WorkspacePage';
import { MarketPage } from '@/pages/MarketPage';
import { MarketConfigPage } from '@/pages/MarketConfigPage';
import { PlanConfigPage } from '@/pages/PlanConfigPage';
import { TenantSubaccountsPage } from '@/pages/TenantSubaccountsPage';
import { TenantRolesPage } from '@/pages/TenantRolesPage';
import { PlatformUsersPage } from '@/pages/PlatformUsersPage';
import { PlatformRolesPage } from '@/pages/PlatformRolesPage';
import { AuditLogsPage } from '@/pages/AuditLogsPage';
import { TenantAuditLogsPage } from '@/pages/TenantAuditLogsPage';
import { PermissionMenuPage } from '@/pages/PermissionMenuPage';
import { ForbiddenPage } from '@/pages/ForbiddenPage';
import {
  RequirePlatformRoute,
  RequireTenantRoute,
} from '@/components/auth/RequireRouteAccess';
import {
  getFirstAccessiblePlatformPath,
  getFirstAccessibleTenantPath,
} from '@/lib/route-access';
import { useAuthStore } from '@/store';
import { usePlatformNavStore } from '@/store/usePlatformNavStore';
import { Toaster } from 'sonner';

function PlatformDefaultRedirect() {
  const perms = useAuthStore((s) => s.user?.permissions ?? []);
  const menuKeys = useAuthStore((s) => s.user?.menuKeys);
  const dynamic = usePlatformNavStore((s) => s.routePermissionOverrides);
  const target = getFirstAccessiblePlatformPath(perms, menuKeys, dynamic);
  if (target) return <Navigate to={target} replace />;
  return <Navigate to="/403" replace state={{ reason: 'none' }} />;
}

function TenantDefaultRedirect() {
  const perms = useAuthStore((s) => s.user?.permissions ?? []);
  const menuKeys = useAuthStore((s) => s.user?.menuKeys);
  const target = getFirstAccessibleTenantPath(perms, menuKeys);
  if (target) return <Navigate to={target} replace />;
  return <Navigate to="/403" replace state={{ reason: 'none' }} />;
}

function App() {
  const { isAuthenticated, user } = useAuthStore();
  const [isLoaded, setIsLoaded] = useState(false);
  const isAdmin = user?.role === 'admin';

  // 等待 Zustand 从 localStorage 加载完成
  useEffect(() => {
    // 直接检查 localStorage，如果有数据就认为已经加载完成
    const hasStoredState = localStorage.getItem('auth-storage') !== null;
    
    // 等待一小段时间让 Zustand 完成初始化
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, hasStoredState ? 0 : 100);

    return () => clearTimeout(timer);
  }, []);

  // 还没加载完成，显示 loading
  if (!isLoaded) {
    return (
      <div className="flex h-full min-h-0 w-full items-center justify-center overflow-y-auto bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
          <p className="mt-4 text-muted-foreground">加载中...</p>
        </div>
      </div>
    );
  }

  // 未登录，显示登录页面
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // 已登录：单容器占满 #root，避免与 Toaster 并列导致高度撑破视口出现整页滚动条
  return (
    <div className="relative h-full min-h-0 w-full overflow-hidden">
      <MainLayout>
        <Routes>
          <Route path="/403" element={<ForbiddenPage />} />
          {/* 管理员路由 */}
          {isAdmin && (
            <>
              <Route path="/" element={<PlatformDefaultRedirect />} />
              <Route
                path="/dashboard"
                element={
                  <RequirePlatformRoute>
                    <DashboardPage />
                  </RequirePlatformRoute>
                }
              />
              <Route
                path="/rbac"
                element={
                  <RequirePlatformRoute>
                    <PermissionMenuPage />
                  </RequirePlatformRoute>
                }
              />
              <Route
                path="/menu-management"
                element={<Navigate to="/rbac" replace />}
              />
              <Route
                path="/order"
                element={
                  <RequirePlatformRoute>
                    <OrderManagementPage />
                  </RequirePlatformRoute>
                }
              />
              <Route
                path="/tenant"
                element={
                  <RequirePlatformRoute>
                    <TenantManagementPage />
                  </RequirePlatformRoute>
                }
              />
              <Route
                path="/market-config"
                element={
                  <RequirePlatformRoute>
                    <MarketConfigPage />
                  </RequirePlatformRoute>
                }
              />
              <Route
                path="/plan-config"
                element={
                  <RequirePlatformRoute>
                    <PlanConfigPage />
                  </RequirePlatformRoute>
                }
              />
              <Route
                path="/platform-roles"
                element={
                  <RequirePlatformRoute>
                    <PlatformRolesPage />
                  </RequirePlatformRoute>
                }
              />
              <Route
                path="/platform-users"
                element={
                  <RequirePlatformRoute>
                    <PlatformUsersPage />
                  </RequirePlatformRoute>
                }
              />
              <Route
                path="/audit-logs"
                element={
                  <RequirePlatformRoute>
                    <AuditLogsPage />
                  </RequirePlatformRoute>
                }
              />
            </>
          )}

          {/* 租户路由 */}
          {!isAdmin && (
            <>
              <Route path="/" element={<TenantDefaultRedirect />} />
              <Route
                path="/workspace"
                element={
                  <RequireTenantRoute>
                    <WorkspacePage />
                  </RequireTenantRoute>
                }
              />
              <Route
                path="/market"
                element={
                  <RequireTenantRoute>
                    <MarketPage />
                  </RequireTenantRoute>
                }
              />
              <Route
                path="/team"
                element={
                  <RequireTenantRoute>
                    <TenantSubaccountsPage />
                  </RequireTenantRoute>
                }
              />
              <Route
                path="/team/roles"
                element={
                  <RequireTenantRoute>
                    <TenantRolesPage />
                  </RequireTenantRoute>
                }
              />
              <Route
                path="/team/audit-logs"
                element={
                  <RequireTenantRoute>
                    <TenantAuditLogsPage />
                  </RequireTenantRoute>
                }
              />
            </>
          )}

          {/* 兜底路由 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </MainLayout>
      <Toaster />
    </div>
  );
}

export default App;
